import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getRateChanges, createRateChange, deleteRateChange } from '../salary/api';
import { fmtDate } from '../salary/formatters';
import type { RateChange } from '../salary/types';
import DataTable from '../../shared/components/DataTable';
const todayIso = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const fmtRate = (v: number | string) => `$${Number(v).toFixed(2)}`;

const RateHistoryPanel: React.FC<{ employeeId: number; onChanged: () => void }> = ({ employeeId, onChanged }) => {
  const { sessionToken } = useAuth();
  const [changes, setChanges] = useState<RateChange[] | null>(null);
  const [err, setErr] = useState('');
  const [effDate, setEffDate] = useState(todayIso());
  const [rate, setRate] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setErr('');
      const res = await getRateChanges(employeeId, sessionToken);
      setChanges(res.rate_changes);
      // Prefill the "new rate" field with the most recent rate the first time.
      if (res.rate_changes.length > 0) {
        setRate((prev) => (prev === '' ? Number(res.rate_changes[0].hourly_rate).toFixed(2) : prev));
      }
    } catch (e: any) { setErr(e.message); }
  }, [employeeId, sessionToken]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setErr('');
    const rateNum = Number(rate);
    if (Number.isNaN(rateNum) || rateNum <= 0) { setErr('New rate must be a positive number'); return; }
    if (!effDate) { setErr('Effective date is required'); return; }
    try {
      setSaving(true);
      await createRateChange(
        { employee_id: employeeId, effective_date: effDate, hourly_rate: rateNum, note: note.trim() || null },
        sessionToken
      );
      setNote('');
      await load();
      onChanged();
    } catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this rate change? Unpaid periods will recalculate.')) return;
    try {
      setErr('');
      await deleteRateChange(id, sessionToken);
      await load();
      onChanged();
    } catch (e: any) { setErr(e.message); }
  };

  const today = todayIso();
  const current = (changes || []).find((c) => c.effective_date.slice(0, 10) <= today);

  return (
    <div className="salary-card" style={{ marginTop: '1rem' }}>
      <h4 style={{ margin: '0 0 0.5rem' }}>Pay rate history</h4>
      {err && <div className="error-banner">{err}</div>}

      <div style={{ marginBottom: '0.75rem', color: '#333' }}>
        Current effective rate: <strong>{current ? fmtRate(current.hourly_rate) : '—'}</strong>
        {current && <span style={{ color: '#666' }}> (since {fmtDate(current.effective_date)})</span>}
      </div>

      <div className="form-row" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block' }}>Effective date</label>
          <input type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)} />
        </div>
        <div>
          <label style={{ display: 'block' }}>New rate ($/hr)</label>
          <input type="number" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} style={{ width: 110 }} />
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={{ display: 'block' }}>Note (optional)</label>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Annual raise" style={{ width: '100%' }} />
        </div>
        <button className="salary-btn" onClick={add} disabled={saving}>{saving ? 'Saving…' : 'Add rate change'}</button>
      </div>
      <small style={{ display: 'block', color: '#666', marginBottom: '0.5rem' }}>
        Rates apply from their effective dates. If a pay period crosses a rate change, split the actual hours between its date ranges. Paid entries stay unchanged.
      </small>

      {changes === null ? (
        <small>Loading…</small>
      ) : changes.length === 0 ? (
        <small style={{ color: '#666' }}>No rate changes recorded.</small>
      ) : (
        <DataTable
          rows={changes}
          rowKey={(c) => c.id}
          caption="Rate changes"
          moreLabel="Note"
          empty="No rate changes recorded."
          rowClass={(c) => (c.effective_date.slice(0, 10) > today ? 'scheduled' : undefined)}
          columns={[
            {
              key: 'effective', header: 'Effective', priority: 'primary',
              render: (c) => (
                <>
                  {fmtDate(c.effective_date)}
                  {c.effective_date.slice(0, 10) > today && <span className="status-badge scheduled"> scheduled</span>}
                </>
              ),
            },
            { key: 'rate', header: 'Rate', priority: 'summary', numeric: true, render: (c) => fmtRate(c.hourly_rate) },
            { key: 'by', header: 'By', priority: 'summary', render: (c) => c.created_by_name || '—' },
            { key: 'note', header: 'Note', priority: 'detail', render: (c) => c.note || '—' },
          ]}
          actions={(c) => <button className="salary-btn secondary" onClick={() => remove(c.id)}>Delete</button>}
        />
      )}
    </div>
  );
};

export default RateHistoryPanel;
